import React, { useEffect, useRef, memo } from "react";
import { CycleRuleDto, CargoType } from "../../../common/data/service-proxies";
import { useForm } from "react-final-form";

interface IDriverDefaultValuesHandler {
  [key: string]: any;
  thirtyMinuteBreakException?: boolean | null;
  twentyFourHourCycleReset?: boolean | null;
  formData: any;
  cycleRules?: Array<CycleRuleDto> | null;
  cargoType?: CargoType | null;
  isEdit: boolean;
}

interface IPropsMapDescription {
  formName: string;
}

interface IPropsMap {
  [key: string]: IPropsMapDescription;
}

const initialPropsMap: IPropsMap = {
  cargoType: { formName: "cargoType" },
  thirtyMinuteBreakException: {
    formName: "brakeException30Minutes",
  },
  twentyFourHourCycleReset: { formName: "cycleReset24Hours" },
  cycleRules: { formName: "cycleRules" },
};

const DriverDefaultValuesHandlerInternal = (
  props: IDriverDefaultValuesHandler
) => {
  const form = useForm();

  // Prepare the map of passed props <-> form prop names
  const propsMap = useRef<IPropsMap>(initialPropsMap);

  // Run this effect only when passed props (selected company) or form changes
  useEffect(() => {
    for (const key in propsMap.current) {
      // For each of the mapped props, check if the form prop is currently empty (or this is a form to add a new record)
      if (
        props[key] &&
        (!props.isEdit || !props.formData[propsMap.current[key].formName])
      ) {
        // If the current prop is not an array, just call a simple form.change with that prop name
        if (!Array.isArray(props[key])) {
          form.change(propsMap.current[key].formName, props[key]);
        } else {
          // The current prop is an array. If the new array's length is higher than or equal to the length of the old array,
          // Just call form.change for each of the array elements, which will overwrite old values and add new ones if required.
          const newArray = props[key] as Array<any>;
          const oldArray: Array<any> =
            form.getFieldState(propsMap.current[key].formName)?.value ?? [];

          if (newArray.length >= oldArray?.length) {
            newArray.forEach((item, index) => {
              const arrayItemKeys = Object.getOwnPropertyNames(item);
              for (const arrayItemKey of arrayItemKeys) {
                const fieldState = form.getFieldState(
                  `${propsMap.current[key].formName}[${index}].${arrayItemKey}`
                );

                if (fieldState) {
                  const { modified } = fieldState;
                  if (modified) {
                    return;
                  }
                }

                form.change(
                  `${propsMap.current[key].formName}[${index}].${arrayItemKey}`,
                  item[arrayItemKey]
                );
              }
            });
          } else {
            // The old array contained more items than the new one contains.
            // We need to call form.change for each existing index to replace each array items.
            // As for array items which don't exist in the new array, just remove them using form.mutators.remove.
            oldArray.forEach((item, index) => {
              const arrayItemKeys = Object.getOwnPropertyNames(item);
              for (const arrayItemKey of arrayItemKeys) {
                if (newArray[index]) {
                  form.change(
                    `${propsMap.current[key].formName}[${index}].${arrayItemKey}`,
                    newArray[index][arrayItemKey]
                  );
                } else {
                  form.mutators.remove(
                    `${propsMap.current[key].formName}`,
                    index
                  );
                }
              }
            });
          }
        }
      }
    }
  }, [props, form]);

  return <></>;
};

export const DriverDefaultValuesHandler = memo(
  DriverDefaultValuesHandlerInternal
);
